import { createAction, createSlice } from '@reduxjs/toolkit';

const initialState = {
    // Per-student test rollup (one row per assessment).
    studentTests: [],
    studentTestsLoading: false,
    studentTestsError: '',
    // Per-student chapter rollup (one row per chapter ever completed).
    studentChapters: [],
    studentChaptersLoading: false,
    studentChaptersError: '',
    // Per-(student, course) assessment summary rollup from ir_assignment_progress.
    assessmentSummary: [],
    assessmentSummaryLoading: false,
    assessmentSummaryError: '',
    // Per-(student, course) computed strengths / weaknesses (top-3 / bottom-3 areas).
    strengthsWeaknesses: { strengths: [], weaknesses: [], scoredAreaCount: 0 },
    strengthsWeaknessesLoading: false,
    strengthsWeaknessesError: '',
    // Attempt history modal state (works for either tests OR chapters; only one open at a time).
    attemptHistory: null,        // { kind: 'test'|'chapter', studentId, ...id, attempts, bestPercentage, ... }
    attemptHistoryLoading: false,
    attemptHistoryError: '',
};

const scoringSlice = createSlice({
    name: 'scoring',
    initialState,
    reducers: {
        setStudentTestsLoading(state, action) {
            state.studentTestsLoading = action.payload;
        },
        setStudentTests(state, action) {
            state.studentTests = action.payload || [];
            state.studentTestsLoading = false;
            state.studentTestsError = '';
        },
        setStudentTestsError(state, action) {
            state.studentTestsError = action.payload || '';
            state.studentTestsLoading = false;
        },
        setAttemptHistoryLoading(state, action) {
            state.attemptHistoryLoading = action.payload;
        },
        setAttemptHistory(state, action) {
            state.attemptHistory = action.payload || null;
            state.attemptHistoryLoading = false;
            state.attemptHistoryError = '';
        },
        setAttemptHistoryError(state, action) {
            state.attemptHistoryError = action.payload || '';
            state.attemptHistoryLoading = false;
        },
        clearAttemptHistory(state) {
            state.attemptHistory = null;
            state.attemptHistoryError = '';
        },
        setStudentChaptersLoading(state, action) {
            state.studentChaptersLoading = action.payload;
        },
        setStudentChapters(state, action) {
            state.studentChapters = action.payload || [];
            state.studentChaptersLoading = false;
            state.studentChaptersError = '';
        },
        setStudentChaptersError(state, action) {
            state.studentChaptersError = action.payload || '';
            state.studentChaptersLoading = false;
        },
        setAssessmentSummaryLoading(state, action) {
            state.assessmentSummaryLoading = action.payload;
        },
        setAssessmentSummary(state, action) {
            state.assessmentSummary = action.payload || [];
            state.assessmentSummaryLoading = false;
            state.assessmentSummaryError = '';
        },
        setAssessmentSummaryError(state, action) {
            state.assessmentSummaryError = action.payload || '';
            state.assessmentSummaryLoading = false;
        },
        setStrengthsWeaknessesLoading(state, action) {
            state.strengthsWeaknessesLoading = action.payload;
        },
        setStrengthsWeaknesses(state, action) {
            state.strengthsWeaknesses = action.payload || { strengths: [], weaknesses: [], scoredAreaCount: 0 };
            state.strengthsWeaknessesLoading = false;
            state.strengthsWeaknessesError = '';
        },
        setStrengthsWeaknessesError(state, action) {
            state.strengthsWeaknessesError = action.payload || '';
            state.strengthsWeaknessesLoading = false;
        },
    },
});

export const scoringActions = {
    loadStudentTests: createAction(`${scoringSlice.name}/loadStudentTests`, (payload) => ({ payload })),
    loadAttemptHistory: createAction(`${scoringSlice.name}/loadAttemptHistory`, (payload) => ({ payload })),
    loadStudentChapters: createAction(`${scoringSlice.name}/loadStudentChapters`, (payload) => ({ payload })),
    loadChapterAttemptHistory: createAction(`${scoringSlice.name}/loadChapterAttemptHistory`, (payload) => ({ payload })),
    loadAssessmentSummary: createAction(`${scoringSlice.name}/loadAssessmentSummary`, (payload) => ({ payload })),
    loadStrengthsWeaknesses: createAction(`${scoringSlice.name}/loadStrengthsWeaknesses`, (payload) => ({ payload })),
    ...scoringSlice.actions,
};

export const getStudentTests = (state) => state.scoring?.studentTests || [];
export const isStudentTestsLoading = (state) => !!state.scoring?.studentTestsLoading;
export const getStudentChapters = (state) => state.scoring?.studentChapters || [];
export const isStudentChaptersLoading = (state) => !!state.scoring?.studentChaptersLoading;
export const getAttemptHistory = (state) => state.scoring?.attemptHistory || null;
export const isAttemptHistoryLoading = (state) => !!state.scoring?.attemptHistoryLoading;
export const getAssessmentSummary = (state) => state.scoring?.assessmentSummary || [];
export const isAssessmentSummaryLoading = (state) => !!state.scoring?.assessmentSummaryLoading;
export const getStrengthsWeaknesses = (state) =>
    state.scoring?.strengthsWeaknesses || { strengths: [], weaknesses: [], scoredAreaCount: 0 };
export const isStrengthsWeaknessesLoading = (state) => !!state.scoring?.strengthsWeaknessesLoading;

export default scoringSlice.reducer;
